import { useState } from 'react';
import { withNamespaces } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Spinner,
} from 'reactstrap';

import Navigator from 'src/components/Navigator';
import { ROUTE_PATH_ADMIN_GUARD_FREIGHT_DETAILS, ROUTE_PATH_ADMIN_GUARD_FREIGHTS_LIST } from 'src/modules/3rd-party-management/constants';
import CompanyData from '../../../components/company-data';
import BidHeader from './components/BidHeader';
import DigitalIdentity from './components/digital-identity';
import DocumentForensics from './components/document-forensics';
import HumanIdentity from './components/human-identity';
import LiveVerification from './components/live-verification';
import Summary from './components/Summary';
import useCarrierGuardBidDetails from './hooks/useCarrierGuardBidDetails';

const GuardBidSummary = ({
    t,
}) => {
    const { freightId, bidId } = useParams();
    const [activeTab, setActiveTab] = useState('live-verification');

    const {
        data: bid,
        isError,
        isFetching,
    } = useCarrierGuardBidDetails({
        freightId,
        bidId,
        t,
    });

    if (isFetching) {
        return (
            <div className="page-content">
                <Container className="py-5 text-center" fluid>
                    <Spinner color="primary" />
                    <p className="mt-2">{t('Loading')}...</p>
                </Container>
            </div>
        );
    }

    if (isError || !bid) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="alert alert-danger" role="alert">
                        {t(isError ? 'Unable to load bid details. Please try again.' : 'Bid not found.')}
                    </div>
                </Container>
            </div>
        );
    }

    const freightDetailsPath = ROUTE_PATH_ADMIN_GUARD_FREIGHT_DETAILS.replace(':freightId', bid.freightId);

    return (
        <div className="page-content carrier-guard-page carrier-guard-bid-summary-page">
            <Container fluid>
                <Row>
                    <Col className="mb-4" lg="12">
                        <Navigator
                            breadCrumbs={[
                                {
                                    title: 'Freights',
                                    link: ROUTE_PATH_ADMIN_GUARD_FREIGHTS_LIST,
                                },
                                {
                                    title: bid.freightReference,
                                    link: freightDetailsPath,
                                },
                                {
                                    title: bid.carrier,
                                    link: '#',
                                },
                            ]}
                        />
                    </Col>
                </Row>

                <BidHeader
                    activeTab={activeTab}
                    bid={bid}
                    onTabChange={setActiveTab}
                />

                {activeTab === 'summary' && (
                    <Summary bid={bid} />
                )}

                {activeTab === 'live-verification' && (
                    <LiveVerification bid={bid} />
                )}

                {activeTab === 'company' && (
                    <CompanyData
                        carrier={{
                            companyReports: bid.companyReports,
                            id: bid.carrierId,
                        }}
                        key={bid.carrierId}
                    />
                )}

                {activeTab === 'documents' && (
                    <DocumentForensics bid={bid} key={bid.id} />
                )}

                {activeTab === 'digital-identity' && (
                    <DigitalIdentity />
                )}

                {activeTab === 'human-identity' && (
                    <HumanIdentity bid={bid} />
                )}

                {activeTab !== 'summary' && activeTab !== 'live-verification' && activeTab !== 'company' && activeTab !== 'documents' && activeTab !== 'digital-identity' && activeTab !== 'human-identity' && (
                    <Card>
                        <CardBody>
                            <div className="alert m-0" role="alert">
                                <p className="text-center mb-0">
                                    {t('This section is empty')}
                                </p>
                            </div>
                        </CardBody>
                    </Card>
                )}
            </Container>
        </div>
    );
};

export default withNamespaces()(
    GuardBidSummary
);
